<template>
  <div class="probability-pro-viz">
    <div class="viz-header-compact" >
       <div class="brief" v-latex="knowledgePoint"></div>
       <div class="formula-chip" v-latex>${{ activeFormula }}$</div>
    </div>

    <div class="split-layout">
      <!-- 仿真区域 -->
      <div class="simulation-stage dark-stage flex-1" style="flex: 1.2;">
        <svg width="100%" height="380" viewBox="0 0 800 600" preserveAspectRatio="xMidYMid meet">
           <defs>
              <filter id="glow-prob"><feGaussianBlur stdDeviation="3" result="blur" /><feComposite in="SourceGraphic" in2="blur" operator="over" /></filter>
           </defs>

           <!-- Grid Background -->
           <g stroke="rgba(241, 245, 249, 0.03)" stroke-width="2">
              <line v-for="i in 17" :key="'v'+i" :x1="i*50" y1="0" :x2="i*50" y2="600" />
              <line v-for="i in 13" :key="'h'+i" x1="0" :y1="i*50" x2="800" :y2="i*50" />
           </g>

           <!-- Mode 1: Venn Diagram (集合关系) -->
           <g v-if="mode === 'venn'" transform="translate(400, 300)">
              <!-- Sample Space Omega -->
              <rect x="-350" y="-220" width="700" height="440" fill="rgba(255,255,255,0.02)" stroke="#475569" stroke-width="2" rx="12" />
              <text x="-330" y="-190" fill="#64748b" font-size="24" font-weight="900">Ω (样本空间)</text>

              <!-- Circle A -->
              <circle :cx="-80" cy="0" :r="radiusA" fill="rgba(56,189,248,0.2)" stroke="#0ea5e9" stroke-width="4" filter="url(#glow-prob)" />
              <text :x="-80" :y="-radiusA - 15" fill="#38bdf8" font-size="24" font-weight="900" text-anchor="middle">事件 A</text>

              <!-- Circle B -->
              <circle :cx="-80 + offsetB" cy="0" :r="radiusB" fill="rgba(244,63,94,0.2)" stroke="#e11d48" stroke-width="4" filter="url(#glow-prob)" />
              <text :x="-80 + offsetB" :y="-radiusB - 15" fill="#f43f5e" font-size="24" font-weight="900" text-anchor="middle">事件 B</text>

              <!-- Interaction Label -->
              <g v-if="hasIntersection" transform="translate(0,0)">
                 <text :x="(-80 + (-80 + offsetB))/2" y="10" fill="#facc15" font-size="20" font-weight="900" text-anchor="middle">A ∩ B</text>
              </g>

              <!-- Complement region helper -->
              <text x="250" y="180" fill="#475569" font-size="18" font-weight="900">Aᶜ ∩ Bᶜ (外部)</text>
           </g>

           <!-- Mode 2: Central Tendency (平均数/中位数) -->
           <g v-if="mode === 'stats'" transform="translate(100, 450)">
              <!-- Data Bars -->
              <g v-for="(val, idx) in dataset" :key="'bar'+idx" :transform="`translate(${idx * 60}, 0)`">
                 <rect x="0" :y="-val * 4" width="45" :height="val * 4" fill="#334155" rx="4" />
                 <text x="22.5" :y="-val * 4 - 10" fill="#94a3b8" font-size="14" text-anchor="middle">{{ val }}</text>
                 <text x="22.5" y="25" fill="#475569" font-size="12" text-anchor="middle">#{ {idx+1} }</text>
              </g>

              <!-- Indicators for Mean & Median -->
              <!-- Mean Line -->
              <line x1="-20" :y1="-dataMean * 4" x2="620" :y2="-dataMean * 4" stroke="#38bdf8" stroke-width="3" stroke-dasharray="8,6" filter="url(#glow-prob)" />
              <text x="630" :y="-dataMean * 4 + 5" fill="#38bdf8" font-size="18" font-weight="900">平均数: {{ dataMean.toFixed(1) }}</text>

              <!-- Median Line -->
              <line x1="-20" :y1="-dataMedian * 4" x2="620" :y2="-dataMedian * 4" stroke="#f43f5e" stroke-width="3" filter="url(#glow-prob)" />
              <text x="630" :y="-dataMedian * 4 + 5" fill="#f43f5e" font-size="18" font-weight="900">中位数: {{ dataMedian.toFixed(1) }}</text>
           </g>

        </svg>
      </div>

      <!-- 右侧解析面板 -->
      <div class="simulation-stage logic-panel">
         <div class="logic-header">概率与描述性统计解算</div>
         <div class="flex-column-center" style="flex:1; padding: 15px;">
            
            <div class="eq-box text-center">
               <div class="eq-display text-blue">实时数据引擎分析</div>
               <div class="seq-data-list">
                  <template v-if="mode === 'venn'">
                     <div class="data-row"><span>P(A) 概率面积比</span><span class="hl text-blue">{{ probA.toFixed(3) }}</span></div>
                     <div class="data-row"><span>P(B) 概率面积比</span><span class="hl text-red">{{ probB.toFixed(3) }}</span></div>
                     <div class="data-row"><span>P(A∩B) 交集概率</span><span class="hl text-yellow">{{ probIntersect.toFixed(3) }}</span></div>
                     <div class="data-row" style="background: rgba(56,189,248,0.1)">
                        <span>P(A∪B) 并集概率</span><span class="hl text-blue">{{ (probA + probB - probIntersect).toFixed(3) }}</span>
                     </div>
                  </template>

                  <template v-if="mode === 'stats'">
                     <div class="data-row"><span>样本容量 (n)</span><span class="hl">{{ dataset.length }}</span></div>
                     <div class="data-row"><span>数值总和 (Σx)</span><span class="hl text-purple">{{ dataSum }}</span></div>
                     <div class="data-row"><span>算术平均数 (μ)</span><span class="hl text-blue">{{ dataMean.toFixed(2) }}</span></div>
                     <div class="data-row" style="background: rgba(244,63,94,0.1)">
                        <span>中位数 (Median)</span><span class="hl text-red">{{ dataMedian }}</span>
                     </div>
                  </template>
               </div>
            </div>

            <div class="eq-box mt-l">
               <p class="desc-text" style="font-size: 13px;"><strong>核心洞察：</strong><br/><br/>{{ tipDesc }}</p>
            </div>
         </div>
      </div>
    </div>

    <!-- 控制台 -->
    <div class="compact-controls">
      <div class="control-card">
         <div class="control-grid-mini">
            <div class="s-item" style="grid-column: span 2;">
               <label>研究模型切换</label>
               <el-radio-group v-model="mode" size="small">
                 <el-radio-button label="venn">事件概率集合 (Venn)</el-radio-button>
                 <el-radio-button label="stats">集中趋势分析 (Mean/Median)</el-radio-button>
               </el-radio-group>
            </div>
            
            <template v-if="mode === 'venn'">
               <div class="s-item">
                  <label>调节事件 A 范围 (半径)</label>
                  <el-slider v-model="radiusA" :min="30" :max="180" size="small"></el-slider>
               </div>
               <div class="s-item">
                  <label>独立性/互斥度 (偏移量)</label>
                  <el-slider v-model="offsetB" :min="0" :max="380" size="small"></el-slider>
               </div>
            </template>

            <template v-if="mode === 'stats'">
               <div class="s-item" style="grid-column: span 2;">
                  <label>动态调整样本数据 (拖动改变首个样本值)</label>
                  <el-slider v-model="dataset[0]" :min="10" :max="100" size="small"></el-slider>
               </div>
               <div class="s-item">
                  <el-button type="primary" size="mini" @click="shuffleData">重新随机生成样本</el-button>
               </div>
            </template>
         </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ProbabilityViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      mode: 'venn',
      radiusA: 100,
      radiusB: 90,
      offsetB: 120,
      dataset: [45, 60, 32, 88, 74, 55, 62, 41, 59, 66]
    }
  },
  mounted() {
    this.detectMode();
  },
  watch: {
    knowledgePoint: {
       handler() { this.detectMode(); },
       immediate: true
    }
  },
  computed: {
    topicIntro() {
       if (this.mode === 'stats') return '集中趋势：平均数与中位数对数据分布的描述能力';
       return '概率与事件：通过维恩图模型理解随机现象的逻辑包含关系';
    },
    activeFormula() {
       if (this.mode === 'stats') return 'x̄ = (Σx_i) / n | Median = x_{(n+1)/2}';
       return 'P(A∪B) = P(A) + P(B) - P(A∩B)';
    },
    // Venn Logic
    hasIntersection() {
       return this.offsetB < (this.radiusA + this.radiusB);
    },
    probA() { return (Math.PI * this.radiusA**2) / (700 * 440); },
    probB() { return (Math.PI * this.radiusB**2) / (700 * 440); },
    probIntersect() {
       if (!this.hasIntersection) return 0;
       // Simplified approximation of circle intersection area for UI
       const d = this.offsetB;
       const r1 = this.radiusA;
       const r2 = this.radiusB;
       if (d >= r1 + r2) return 0;
       if (d <= Math.abs(r1 - r2)) return Math.min(this.probA, this.probB);
       
       let area = (r1+r2-d) * (r1+r2-d) * 0.5; // very rough mock for visual feedback only
       return Math.min(area/1000, this.probA, this.probB);
    },
    // Stats Logic
    dataSum() { return this.dataset.reduce((a, b) => a + b, 0); },
    dataMean() { return this.dataSum / this.dataset.length; },
    dataMedian() {
       const sorted = [...this.dataset].sort((a,b) => a-b);
       const mid = Math.floor(sorted.length / 2);
       return sorted.length % 2 !== 0 ? sorted[mid] : (sorted[mid-1] + sorted[mid]) / 2;
    },
    tipDesc() {
       if (this.mode === 'stats') return '平均数(Mean)对异常值极其敏感，一个极端高分就能拉高全班平均分。而中位数(Median)则稳如泰山，它只关注谁排在正中间。在财富分配或收入调查中，中位数往往比平均数更能反映真实的大众情况。';
       return '概率的加法公式 P(A∪B) 之所以要减去 P(A∩B)，是因为在分别计算 A 和 B 的概率时，重合的部分被重复计算了两次。当两个事件互斥（即交集为0）时，加法公式就退化成了最简单的 P(A) + P(B)。';
    }
  },
  methods: {
    detectMode() {
      const kp = this.knowledgePoint || '';
      if (/平均数|中位数|统计/.test(kp)) {
         this.mode = 'stats';
      } else {
         this.mode = 'venn';
      }
    },
    shuffleData() {
       this.dataset = this.dataset.map(() => Math.floor(Math.random() * 80) + 10);
    }
  }
}
</script>

<style scoped lang="scss">
.probability-pro-viz { flex: 1; display: flex; flex-direction: column; gap: 12px; height: 100%; overflow-y: auto; overflow-x: hidden; padding-right: 5px; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 15px; color: #1e293b; font-weight: 700; } .formula-chip { background: #eff6ff; color: #1d4ed8; border: 1px solid #bfdbfe; padding: 8px 18px; border-radius: 6px; font-weight: 900; font-family: -apple-system, sans-serif; font-size: 18px; } }
.split-layout { display: flex; gap: 12px; flex: 1; min-height: 360px; }
.flex-1 { flex: 1; min-width: 0; }
.logic-panel { flex: 0.8; min-width: 0; display: flex; flex-direction: column; background: #0f172a !important; border-radius: 12px; }
.logic-header { background: #1e293b; padding: 12px; color: #38bdf8; font-size: 14px; font-weight: 900; text-align: center; border-bottom: 2px solid #0f172a; border-radius: 12px 12px 0 0; }
.flex-column-center { display: flex; flex-direction: column; justify-content: flex-start; overflow-y: auto; }

.eq-box { width: 100%; border-radius: 8px; overflow: hidden; }
.eq-display { background: rgba(0,0,0,0.4); padding: 10px; font-weight: 900; border-bottom: 1px solid rgba(255,255,255,0.05); }
.seq-data-list { display: flex; flex-direction: column; background: rgba(15, 23, 42, 0.6); }
.data-row { display: flex; justify-content: space-between; align-items: center; padding: 12px 18px; border-bottom: 1px solid rgba(255,255,255,0.02); color: #94a3b8; font-size: 13px; font-weight: bold; }
.hl { color: #f8fafc; font-weight: 900; font-family: monospace; font-size: 14px; }

.text-yellow { color: #facc15; } .text-green { color: #34d399; } .text-blue { color: #38bdf8; } .text-purple { color: #a855f7; } .text-red { color: #f43f5e; }
.desc-text { color: #cbd5e1; font-size: 13px; line-height: 1.6; padding: 16px; background: rgba(56, 189, 248, 0.05); border-radius: 8px; border: 1px solid rgba(56, 189, 248, 0.1); }
.mt-l { margin-top: 25px; }

.simulation-stage { background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }

.compact-controls { flex: 0; display: flex; flex-direction: column; gap: 10px; }
.control-card { background: #ffffff; padding: 16px 20px; border-radius: 12px; border: 1px solid #e2e8f0; }
.control-grid-mini { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; .s-item { label { font-size: 13px; color: #475569; font-weight: 900; margin-bottom: 8px; display: block; } } }
</style>